import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import {
  shareBrief as shareBriefApi,
  ShareBriefPayload,
} from '@disco/api/briefs'
import { parseServerError } from '@disco/utils/captureError'

/**
 * Data structure for sharing a brief
 */
interface ShareBriefData {
  /** Array of recipient email addresses */
  recipients: string[]
  /** Reply-to email address for the shared brief */
  replyToAddress: string
  /** Sender display name */
  senderName: string
  /** Email subject line */
  subject: string
  /** Email message body */
  message: string
}

/**
 * Result structure returned from brief sharing operation
 */
interface BriefShareResult {
  /** Whether the sharing operation was successful */
  success: boolean
  /** Optional ID of the created share (when successful) */
  shareId?: number
  /** Optional error message (when unsuccessful) */
  error?: string
}

export const useShareBrief = () => {
  const shareBrief = useCallback(
    async (
      briefId: number,
      shareData: ShareBriefData,
    ): Promise<BriefShareResult> => {
      try {
        // Validate input data
        if (!shareData.recipients || shareData.recipients.length === 0) {
          throw new Error('At least one recipient is required')
        }

        if (!shareData.subject || !shareData.subject.trim()) {
          throw new Error('Subject is required')
        }

        // Build API payload according to the API specification
        const payload: ShareBriefPayload = {
          recipients: shareData.recipients.map(email => ({
            type: 'email',
            email: email.trim(),
          })),
          subject: shareData.subject.trim(),
        }

        // Add optional fields if provided
        if (shareData.replyToAddress && shareData.replyToAddress.trim()) {
          payload.reply_to = shareData.replyToAddress.trim()
        }

        if (shareData.senderName && shareData.senderName.trim()) {
          payload.sender_name = shareData.senderName.trim()
        }

        if (shareData.message && shareData.message.trim()) {
          payload.message = shareData.message.trim()
        }

        // Call the API
        await shareBriefApi(briefId, payload)

        return {
          success: true,
        }
      } catch (error) {
        console.error('Error sharing brief:', error)

        // Parse server error for better error messages
        const errorMessage = parseServerError(error?.body || error)

        return {
          success: false,
          error: errorMessage || 'Failed to share brief',
        }
      }
    },
    [],
  )

  return { shareBrief }
}
